import * as React from 'react';

import { Container } from '../../layout/Container';
import { Heading } from '../../ui/text/Heading';
import { Paragraph } from '../../ui/text/Paragraph';
import { Section } from '../../layout/Section';
import { BackgroundLoad } from '../../layout/BackgroundLoad';
import { useFadeTransition } from '../../../utils/hooks/useFadeTransition';
import { Seo } from '../../../utils/seo/Index';
import { RouteComponentProps } from 'react-router';
import { useFormTransition } from '../../../utils/hooks/useFormTransition';
import { useMutation } from '@apollo/client';
import { Mail, MailVariables } from '../../../__types__/generated/Mail';
import { MAIL_MUTATION } from '../../../../client/__graphql__/mutations/mail';
import { Thanks } from '../../layout/Thanks';
import { useForm } from '../../../utils/hooks/useForm';
import styled from 'styled-components';
import { Form, FormRow } from '../../ui/form/Form';
import { FormError } from '../../ui/form/FormError';
import { Input } from '../../ui/form/Input';
import { ButtonGroup } from '../../ui/button/ButtonGroup';
import { Button } from '../../ui/button/Button';
import { ButtonLink } from '../../ui/link/ButtonLink';
import { routeConfig } from '../../../__config__/routes';
import { animated } from 'react-spring';

const AboutUsConcept: React.FC<RouteComponentProps> = ({ location }) => {
  const [posted, setPosted] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const { fadeIn, fadeOut } = useFormTransition({
    posted
  });

  const [mail, { error }] = useMutation<Mail, MailVariables>(MAIL_MUTATION, {
    onCompleted: ({ lead }) => {
      setLoading(false);
      if (lead?.mail?.success) {
        setPosted(true);
      }
    }
  });

  React.useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const { fields, getFields, handleSubmit, handleChange } = useForm({
    fields: [
      {
        type: 'text',
        name: 'name',
        placeholder: 'Ola Nordmann',
        value: '',
        label: 'Navn'
      },
      {
        type: 'text',
        name: 'address',
        placeholder: 'Eks. Adresseveien 23',
        value: '',
        label: 'Adresse'
      },
      {
        type: 'text',
        name: 'zip',
        placeholder: 'Eks. 1234',
        value: '',
        label: 'Postnummer *',
        required: true
      },
      {
        type: 'tel',
        name: 'phone',
        placeholder: 'Eks. 00000000',
        value: '',
        label: 'Telefon *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      },
      {
        type: 'email',
        name: 'email',
        placeholder: 'Eks. ole@mail.no',
        value: '',
        label: 'E-post *',
        labelAfter: '(Brukes til å kontakte deg)',
        required: true
      }
    ],
    submit: () => {
      const preparedFields: any = getFields(fields);
      setLoading(true);

      mail({
        variables: {
          input: {
            ...preparedFields,
            referer: `${location.pathname}${location.search}`,
            leadType: 'EPOST',
            mailType: 150
          }
        }
      });
    }
  });

  return (
    <>
      <Seo
        title="Mer om oss - PrivatMegleren"
        description="PrivatMegleren består av engasjerte og kompetanserike meglere. Når du velger PrivatMegleren, kan du også forvente mer av den megleren som bistår deg."
        url={location.pathname}
      />
      <Container style={{ paddingTop: '100px', minHeight: '1000px' }}>
        <Section>
          <Heading tag="h1" center={true}>
            PrivatMegleren
          </Heading>
          <Paragraph>
            PrivatMegleren består av engasjerte og kompetanserike meglere. Når
            du velger PrivatMegleren, kan du også forvente mer av den megleren
            som bistår deg. Hvert lille steg i prosessen er tilpasset akkurat
            deg og din eiendom.
          </Paragraph>
          <Paragraph>
            For de fleste av oss er kjøp og salg av eiendom en sjelden
            anledning. Det handler om kanskje det mest dyrebare man kan kjøpe
            for penger gjennom livet. Derfor er det viktig å ta seg god tid til
            å velge en megler som kan prestere på det høyeste nivået.
          </Paragraph>
          <Paragraph>Velkommen til PrivatMegleren.</Paragraph>
          <Heading tag="h2">
            Felles ambisjon – høyest pris for din eiendom
          </Heading>
          <GridContainer>
            <EmbedContainer>
              <iframe
                className="VimeoOuterContainer"
                src="https://player.vimeo.com/video/1114252852"
                frameBorder="0"
                width="100%"
                height="100%"
                allow="fullscreen; picture-in-picture"
              ></iframe>
            </EmbedContainer>
            <EmbedContainer>
              <iframe
                className="VimeoOuterContainer"
                src="https://player.vimeo.com/video/1114250760"
                frameBorder="0"
                width="100%"
                height="100%"
                allow="fullscreen; picture-in-picture"
              ></iframe>
            </EmbedContainer>
            <EmbedContainer>
              <iframe
                className="VimeoOuterContainer"
                src="https://player.vimeo.com/video/1114260817"
                frameBorder="0"
                width="100%"
                height="100%"
                allow="fullscreen; picture-in-picture"
              ></iframe>
            </EmbedContainer>
            <EmbedContainer>
              <iframe
                className="VimeoOuterContainer"
                src="https://player.vimeo.com/video/1114261404"
                frameBorder="0"
                width="100%"
                height="100%"
                allow="fullscreen; picture-in-picture"
              ></iframe>
            </EmbedContainer>
          </GridContainer>
          <Paragraph>
            Prisen som oppnås på din eiendom er avgjørende også for oss. Derfor
            bruker vi mye tid på å avklare hvor akkurat dine potensielle kjøpere
            ferdes og hvordan de tenker. Vi skal ikke bare presentere en
            eiendom. Vi skal presentere et hjem andre har lyst på. Derfor er
            markedsføring og kanalvalg avgjørende for å få flere interessenter.
            Vi vil ha kamp mellom flere kjøpere om nettopp din eiendom.
          </Paragraph>
          <Heading tag="h2">Vi gjør ting annerledes – her er hvordan</Heading>
          <Paragraph>
            Vi sier ikke bare at PrivatMegleren utgjør en forskjell. Vi viser
            det også i praksis. I tillegg til standardisert materiell enhver
            eiendomsmegler bruker, har PrivatMegleren flere verktøyer som
            optimaliserer salgsprosessen. Les mer om disse verktøyene i
            prosessens detaljer.
          </Paragraph>

          <FormWrapper style={{ ...fadeOut, padding: '2em 2em' }}>
            <Section className="form-section" style={{ paddingBottom: '10px' }}>
              <Heading tag="h2" center={true}>
                Vil du vite mer?
              </Heading>
              <Paragraph style={{ textAlign: 'center' }}>
                Fyll inn skjema under og du vil bli kontaktet.
              </Paragraph>
              <Form onSubmit={handleSubmit} noValidate>
                {error &&
                  error.graphQLErrors.map((error, index) => (
                    <FormError key={`error_${index}`} message={error.message} />
                  ))}
                {fields.map((item, index) => {
                  return (
                    <FormRow key={`field_${index}`}>
                      <Input
                        type={item?.type}
                        name={item?.name}
                        label={item?.label}
                        labelAfter={item?.labelAfter}
                        placeholder={item?.placeholder}
                        value={item?.value}
                        onChange={handleChange}
                        error={item?.error}
                      />
                    </FormRow>
                  );
                })}

                <ButtonGroup>
                  <Button
                    type="primary"
                    colorTheme="gold"
                    disabled={loading}
                    loading={loading}
                  >
                    Send inn
                  </Button>
                  <ButtonLink
                    as="a"
                    href={routeConfig?.personvern.url}
                    className="privacy-link-mobile"
                  >
                    Personvernpolicy
                  </ButtonLink>
                </ButtonGroup>
              </Form>
            </Section>
          </FormWrapper>

          <Thanks style={fadeIn}>
            <Section className="form-section">
              <Heading tag="h2" center={true}>
                Takk for interessen.
              </Heading>
            </Section>
          </Thanks>
        </Section>
        <BackgroundLoad
          opacity={0.4}
          position="center bottom"
          url="https://cdn.reeltime.no/pm_assets/img/bakgrunner/hjort_bakside2.jpg"
        />
      </Container>
    </>
  );
};
const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 20px;
  margin-top: 20px;
`;

const EmbedContainer = styled.div`
  position: relative;
  padding: 58.07% 0 0 0;
  .VimeoOuterContainer {
    position: absolute;
    top: 0;
    left: 0;
    padding-bottom: 4em;
  }

  @media all and (max-width: 1200px) {
    padding: 73.07% 0 0 0;
    margin-top: 0px;
    .VimeoOuterContainer {
      padding-bottom: 4em;
    }
  }
`;

const FormWrapper = styled(animated.div)``;

export default AboutUsConcept;
